'use client';

import { Download, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { cn } from '@/app/utils';
import { ActionButton } from './ActionButton';

/**
 * 媒体水印下载 UI，自内向外三层：Options（选项列表）→ HoverCard（共享外壳，交互单点、
 * 触发按钮外注）→ Button（卡片/Grid 场景封装）。文件页 header 直接复用 HoverCard 自绘触发器。
 */

interface WatermarkDownloadOptionsProps {
  withWatermark: boolean;
  onChange: (withWatermark: boolean) => void;
}

const WatermarkDownloadOptions = ({ withWatermark, onChange }: WatermarkDownloadOptionsProps) => {
  const t = useTranslations();

  return (
    <HoverCardContent className="w-fit bg-system-bg02 p-1 text-sm" align="center" sideOffset={2} side="top">
      {[
        { value: true, label: t('claw.assets.actions.downloadWithWatermark') },
        { value: false, label: t('claw.assets.actions.downloadNoWatermark') },
      ].map(({ value, label }) => (
        <div
          key={label}
          className={cn(
            'flex cursor-pointer items-center rounded-4 px-3 py-1.5 text-system-text01 hover:bg-system-hover01',
            withWatermark === value && 'bg-system-hover01',
          )}
          onClick={() => onChange(value)}
        >
          <div className="mr-2 flex size-4 shrink-0 items-center justify-center rounded-full border border-system-white64">
            {withWatermark === value && <div className="size-2 rounded-full bg-current" />}
          </div>
          <span className="whitespace-nowrap">{label}</span>
        </div>
      ))}
      <HoverCardArrow />
    </HoverCardContent>
  );
};

interface WatermarkDownloadHoverCardProps {
  withWatermark: boolean;
  onWithWatermarkChange: (withWatermark: boolean) => void;
  /** 水印下载进行中：强制收起悬浮卡，并透传给触发器做禁用/spinner。 */
  downloading: boolean;
  onDownload: () => void;
  onWatermarkDownload: () => void;
  /** onClick 已封装好按 withWatermark 分发下载；downloading 见上。 */
  renderTrigger: (trigger: { downloading: boolean; onClick: () => void }) => ReactNode;
}

/**
 * 水印下载悬浮卡：hover 展开「带/不带水印」二选一，点击按当前选择分发下载。
 */
export const WatermarkDownloadHoverCard = ({
  withWatermark,
  onWithWatermarkChange,
  downloading,
  onDownload,
  onWatermarkDownload,
  renderTrigger,
}: WatermarkDownloadHoverCardProps) => (
  <HoverCard openDelay={0} closeDelay={100} open={downloading ? false : undefined}>
    <HoverCardTrigger asChild>
      {renderTrigger({
        downloading,
        onClick: () => (withWatermark ? onWatermarkDownload() : onDownload()),
      })}
    </HoverCardTrigger>
    <HoverCardPortal>
      <WatermarkDownloadOptions withWatermark={withWatermark} onChange={onWithWatermarkChange} />
    </HoverCardPortal>
  </HoverCard>
);

interface WatermarkDownloadButtonProps {
  /** true 时展示「带/不带水印」二选一悬浮卡（仅中国站且是可预览媒体）；false 退化成普通下载按钮。 */
  showWatermarkOption: boolean;
  /** 当前项水印下载进行中：禁用按钮、显示 spinner，并强制收起悬浮卡。 */
  downloading?: boolean;
  onDownload: () => void;
  onWatermarkDownload: () => void;
}

/**
 * Grid/卡片场景的下载入口：showWatermarkOption 为假时退化成普通下载按钮，
 * 为真时自持 withWatermark 选择态，套 WatermarkDownloadHoverCard。
 */
export const WatermarkDownloadButton = ({
  showWatermarkOption,
  downloading = false,
  onDownload,
  onWatermarkDownload,
}: WatermarkDownloadButtonProps) => {
  const t = useTranslations();
  const [withWatermark, setWithWatermark] = useState(false);

  if (!showWatermarkOption) {
    return (
      <ActionButton
        tooltip={t('claw.assets.actions.download')}
        icon={<Download className="size-4" />}
        onClick={onDownload}
      />
    );
  }

  return (
    <WatermarkDownloadHoverCard
      withWatermark={withWatermark}
      onWithWatermarkChange={setWithWatermark}
      downloading={downloading}
      onDownload={onDownload}
      onWatermarkDownload={onWatermarkDownload}
      renderTrigger={({ downloading: isDownloading, onClick }) => (
        <Button
          variant="text"
          size="sm"
          disabled={isDownloading}
          className="h-auto rounded-8 p-2 text-system-white40 hover:bg-system-white08 hover:text-system-white disabled:opacity-40"
          onClick={onClick}
        >
          {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        </Button>
      )}
    />
  );
};
